import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, Copy, ExternalLink, Loader2, LockKeyhole, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { supabase } from "../supabaseClient";
import { workspaceNameSlug } from "../institutionWorkspaceSlug";

const DEFAULT_SETTINGS = {
  primaryColor: "#0C69C8",
  accentColor: "#D5A419",
  supportColor: "",
  highlightColor: "",
  backgroundColor: "#070A12",
  useAppLeaderboardBranding: false,
  sections: ["stats", "members", "teams"],
};

const DEFAULT_APP_LEADERBOARD_SETTINGS = {
  primaryColor: "#0C69C8",
  accentColor: "#D5A419",
  applyBranding: true,
};
const normalizeHexInput = (value, fallback) => /^#[0-9A-Fa-f]{6}$/.test(String(value || "").trim()) ? String(value).trim() : fallback;
const normalizeOptionalHexInput = (value) => {
  const cleaned = String(value || "").trim();
  return !cleaned || /^#[0-9A-Fa-f]{6}$/.test(cleaned) ? cleaned : "";
};
const isLightColor = (hex = "") => {
  const match = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return false;
  const value = parseInt(match[1], 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return ((0.2126 * r) + (0.7152 * g) + (0.0722 * b)) > 168;
};
const logoUrlError = (logoUrl) => {
  if (!logoUrl) return "";
  try {
    const url = new URL(logoUrl);
    return ["http:", "https:"].includes(url.protocol) ? "" : "Use an http:// or https:// image URL.";
  } catch {
    return "Enter a complete image URL, including https://.";
  }
};
const slugError = (slug) => {
  if (!slug) return "Could not create the workspace dashboard link.";
  if (slug.length < 3 || slug.length > 80) return "Could not create a valid workspace dashboard link.";
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return "Could not create a valid workspace dashboard link.";
  return "";
};

const inputStyle = {
  width: "100%",
  border: "1px solid rgba(148,163,184,0.35)",
  borderRadius: 9,
  padding: "9px 10px",
  font: "inherit",
};

const colorTextStyle = (C) => ({
  ...inputStyle,
  padding: "8px 9px",
  fontSize: 12,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  color: C.text,
  background: C.bgElevated,
});

export default function InstitutionLiveDashboardSettings({ workspaceId, C, appLeaderboardSettings }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);
  const [logoTouched, setLogoTouched] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ enabled: false, visibility: "members", slug: "", settings: DEFAULT_SETTINGS });
  const resolvedAppLeaderboardSettings = useMemo(() => ({
    ...DEFAULT_APP_LEADERBOARD_SETTINGS,
    ...(appLeaderboardSettings || {}),
  }), [appLeaderboardSettings]);
  const usesAppLeaderboardBranding = form.settings?.useAppLeaderboardBranding === true;
  const effectiveSettings = useMemo(() => {
    const settings = form.settings || DEFAULT_SETTINGS;
    if (!usesAppLeaderboardBranding) return settings;
    return {
      ...settings,
      primaryColor: resolvedAppLeaderboardSettings.primaryColor || DEFAULT_SETTINGS.primaryColor,
      accentColor: resolvedAppLeaderboardSettings.accentColor || DEFAULT_SETTINGS.accentColor,
    };
  }, [form.settings, resolvedAppLeaderboardSettings, usesAppLeaderboardBranding]);
  const previewIsLight = isLightColor(effectiveSettings.backgroundColor);
  const previewText = previewIsLight ? "#202124" : "#FFFFFF";
  const previewMuted = previewIsLight ? "rgba(32,33,36,0.66)" : "rgba(255,255,255,0.72)";
  const previewSupport = effectiveSettings.supportColor || effectiveSettings.primaryColor;
  const previewHighlight = effectiveSettings.highlightColor || effectiveSettings.accentColor;

  useEffect(() => {
    if (!workspaceId) return;
    let active = true;
    setLoading(true);
    setLoadError("");
    supabase.rpc("get_institution_live_dashboard_admin", { p_workspace_id: workspaceId })
      .then(async ({ data, error }) => {
        if (!active) return;
        if (error) throw error;
        const { data: workspace, error: workspaceError } = await supabase
          .from("institution_workspaces")
          .select("logo_url")
          .eq("id", workspaceId)
          .single();
        if (workspaceError) throw workspaceError;
        const settings = { ...DEFAULT_SETTINGS, ...(data?.settings || {}), logoUrl: workspace?.logo_url || "" };
        setState(data);
        setForm({
          enabled: data?.enabled === true,
          visibility: data?.visibility || "members",
          slug: workspaceNameSlug(data?.workspace_name),
          settings: { ...settings, sections: settings.sections?.length ? settings.sections : DEFAULT_SETTINGS.sections },
        });
      })
      .catch((error) => active && setLoadError(error?.message || "Could not load live dashboard settings."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [loadAttempt, workspaceId]);

  const publishedSlug = workspaceNameSlug(state?.workspace_name || form.slug);
  const publishedUrl = state?.enabled && publishedSlug ? `${window.location.origin}/institutions/${publishedSlug}` : "";
  const liveDashboardPlansPath = `/institutions/create?feature=live-dashboard&workspace_id=${encodeURIComponent(workspaceId)}`;
  const currentSlugError = slugError(form.slug);
  const currentLogoUrlError = logoUrlError(form.settings?.logoUrl);
  const currentOptionalColorError = [
    ["supportColor", "Support"],
    ["highlightColor", "Highlight"],
  ].map(([key, label]) => {
    const value = String(form.settings?.[key] || "").trim();
    return value && !/^#[0-9A-Fa-f]{6}$/.test(value) ? `${label} colour must use a six-digit hex value, such as #FBBC05.` : "";
  }).find(Boolean) || "";
  const shownLogoUrlError = (logoTouched || saveError) && currentLogoUrlError;
  const previewLogoUrl = form.settings?.logoUrl || state?.workspace_logo_url || "";
  const isPublished = state?.enabled === true && Boolean(state?.slug);
  const updateSetting = (key, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, settings: { ...current.settings, [key]: value } }));
  };
  const copyDashboardUrl = async () => {
    if (!publishedUrl) return;
    try {
      await navigator.clipboard.writeText(publishedUrl);
      toast.success("Dashboard link copied.");
    } catch {
      toast.error("Could not copy the link. Open the live page and copy the address from your browser.");
    }
  };
  const resetLiveDefaults = () => setForm((current) => ({
    ...current,
    settings: { ...DEFAULT_SETTINGS, logoUrl: current.settings?.logoUrl || "", sections: [...DEFAULT_SETTINGS.sections] },
  }));
  const setUseAppLeaderboardBranding = (value) => setForm((current) => ({
    ...current,
    settings: {
      ...current.settings,
      useAppLeaderboardBranding: value,
      ...(value ? {
        primaryColor: resolvedAppLeaderboardSettings.primaryColor || DEFAULT_SETTINGS.primaryColor,
        accentColor: resolvedAppLeaderboardSettings.accentColor || DEFAULT_SETTINGS.accentColor,
      } : {}),
    },
  }));
  const save = async () => {
    if (currentSlugError || currentLogoUrlError || currentOptionalColorError) {
      setSaveError(currentSlugError || currentLogoUrlError || currentOptionalColorError);
      return;
    }
    setSaving(true);
    setSaved(false);
    setSaveError("");
    try {
      const cleanFormSettings = { ...(form.settings || {}) };
      const logoUrl = cleanFormSettings.logoUrl || null;
      delete cleanFormSettings.logoUrl;
      const settingsToSave = {
        ...cleanFormSettings,
        ...(usesAppLeaderboardBranding ? {
          primaryColor: resolvedAppLeaderboardSettings.primaryColor || DEFAULT_SETTINGS.primaryColor,
          accentColor: resolvedAppLeaderboardSettings.accentColor || DEFAULT_SETTINGS.accentColor,
        } : {}),
      };
      const { error: logoError } = await supabase
        .from("institution_workspaces")
        .update({ logo_url: logoUrl })
        .eq("id", workspaceId);
      if (logoError) throw logoError;
      const { data, error } = await supabase.rpc("update_institution_live_dashboard", {
        p_workspace_id: workspaceId,
        p_enabled: form.enabled,
        p_visibility: form.visibility,
        p_slug: publishedSlug,
        p_settings: settingsToSave,
      });
      if (error) throw error;
      const { error: settingsError } = await supabase
        .from("institution_workspaces")
        .update({ live_dashboard_settings: settingsToSave })
        .eq("id", workspaceId);
      if (settingsError) throw settingsError;
      setState(data ? { ...data, settings: settingsToSave } : data);
      setForm((current) => ({ ...current, settings: { ...settingsToSave, logoUrl: current.settings?.logoUrl || "" } }));
      setLogoTouched(false);
      setSaved(true);
      toast.success("Live dashboard settings saved.");
    } catch (error) {
      const message = error?.message || "Could not save live dashboard settings.";
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!workspaceId) return null;
  if (loading) {
    return (
      <section aria-label="Live dashboard" aria-busy="true" style={{ display: "flex", alignItems: "center", gap: 9, padding: 14, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.textMuted, fontSize: 13 }}>
        <Loader2 aria-hidden="true" size={17} style={{ animation: "spin 1s linear infinite" }} />
        Checking live dashboard access...
      </section>
    );
  }
  if (loadError) {
    return (
      <section aria-label="Live dashboard" role="alert" style={{ padding: 14, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
        <strong style={{ color: C.text }}>Live dashboard settings could not load</strong>
        <p style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.55, margin: "6px 0 10px" }}>{loadError}</p>
        <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)} style={{ minHeight: 38, padding: "8px 12px", borderRadius: 9, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, fontSize: 13, fontWeight: 900, cursor: "pointer" }}>
          Try again
        </button>
      </section>
    );
  }
  if (!state?.has_access) {
    return (
      <section aria-label="Live dashboard" style={{ padding: 14, borderRadius: 12, border: `1px solid ${C.border}`, background: C.bgAlt }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LockKeyhole aria-hidden="true" size={16} style={{ color: C.accent }} />
          <strong style={{ color: C.text }}>Live dashboard is locked</strong>
        </div>
        <p style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.55, margin: "7px 0 11px" }}>
          This workspace does not have an active live dashboard entitlement. Upgrade to Club Pro, Club Elite, or a business plan to publish a branded leaderboard for members or anyone with your link.
        </p>
        <Link to={liveDashboardPlansPath} style={{ display: "inline-flex", alignItems: "center", minHeight: 40, padding: "8px 12px", borderRadius: 9, background: C.accent, border: `1px solid ${C.accent}`, color: "#fff", fontSize: 13, fontWeight: 900, textDecoration: "none" }}>
          View plans with live dashboard
        </Link>
      </section>
    );
  }

  return (
    <section aria-label="Live dashboard" style={{ display: "grid", gap: 13, padding: 14, borderRadius: 12, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <strong style={{ color: C.text }}>Live dashboard</strong>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 6, color: isPublished ? C.success : C.textMuted, fontSize: 12, fontWeight: 850 }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: isPublished ? C.success : C.textMuted }} />
            {isPublished ? `Published for ${state.visibility === "public" ? "anyone with the link" : "signed-in workspace members"}` : "Not published yet"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {publishedUrl && (
            <a
              href={publishedUrl}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 38, padding: "8px 12px", borderRadius: 9, background: C.accent, border: `1px solid ${C.accent}`, color: "#fff", fontWeight: 900, fontSize: 13, textDecoration: "none" }}
            >
              <ExternalLink aria-hidden="true" size={15} /> Open page
            </a>
          )}
          <button
            type="button"
            onClick={() => setSettingsOpen((open) => !open)}
            aria-expanded={settingsOpen}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 38, padding: "8px 12px", borderRadius: 9, background: C.bgAlt, border: `1px solid ${C.accentBorder}`, color: C.accent, fontWeight: 900, fontSize: 13, cursor: "pointer" }}
          >
            <Settings2 aria-hidden="true" size={15} /> {settingsOpen ? "Close settings" : isPublished ? "Edit settings" : "Set up dashboard"}
          </button>
        </div>
      </div>
      {!settingsOpen && publishedUrl && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", padding: 10, borderRadius: 10, background: C.bgAlt, border: `1px solid ${C.border}` }}>
          <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 800, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{publishedUrl}</span>
          <button
            type="button"
            onClick={copyDashboardUrl}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34, flexShrink: 0, padding: "7px 11px", borderRadius: 9, border: `1px solid ${C.accentBorder}`, background: C.accentBgSubtle, color: C.accent, fontSize: 12, fontWeight: 900, cursor: "pointer" }}
          >
            <Copy aria-hidden="true" size={14} /> Copy link
          </button>
        </div>
      )}
      {settingsOpen && (
        <div style={{ display: "grid", gap: 14, paddingTop: 2 }}>
          <section style={{ padding: 12, borderRadius: 10, background: C.bgAlt, border: `1px solid ${C.border}` }}>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", color: C.textSecondary, fontSize: 13, fontWeight: 800 }}>
              <input type="checkbox" checked={form.enabled} onChange={(event) => { setSaved(false); setForm((current) => ({ ...current, enabled: event.target.checked })); }} style={{ marginTop: 2 }} />
              <span>
                Publish live dashboard
                <span style={{ display: "block", color: C.textMuted, fontSize: 12, fontWeight: 600, lineHeight: 1.5, marginTop: 3 }}>Make your workspace leaderboard available to the audience you select.</span>
              </span>
            </label>
          </section>

          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend style={{ color: C.textSecondary, fontSize: 13, fontWeight: 850, marginBottom: 7 }}>Who can view it?</legend>
            <div style={{ display: "grid", gap: 7 }}>
              {[
                ["members", "Signed-in workspace members", "Best for private leaderboards."],
                ["public", "Anyone with the link", "Anyone with the link can see member names, profile photos, and step totals. Share carefully, especially for schools."],
              ].map(([value, label, description]) => (
                <label key={value} style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: 10, borderRadius: 9, border: `1px solid ${form.visibility === value ? C.accentBorder : C.border}`, background: form.visibility === value ? C.accentBgSubtle : C.bgAlt, cursor: "pointer" }}>
                  <input type="radio" name="dashboard-visibility" checked={form.visibility === value} onChange={() => { setSaved(false); setForm((current) => ({ ...current, visibility: value })); }} style={{ marginTop: 2 }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", color: C.textSecondary, fontSize: 13, fontWeight: 800 }}>{label}</span>
                    <span style={{ display: "block", color: C.textMuted, fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>{description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <section aria-label="Live dashboard branding" style={{ borderRadius: 10, border: `1px solid ${C.border}`, background: C.bgAlt, overflow: "hidden" }}>
            <button type="button" onClick={() => setBrandingOpen((open) => !open)} aria-expanded={brandingOpen} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 10, padding: 12, border: 0, background: "transparent", color: C.textSecondary, cursor: "pointer", textAlign: "left" }}>
              <span>
                <span style={{ display: "block", fontSize: 13, fontWeight: 850 }}>Customize branding</span>
                <span style={{ display: "block", color: C.textMuted, fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>Optional colours and logo preview for the live web page.</span>
              </span>
              <ChevronDown aria-hidden="true" size={18} style={{ flexShrink: 0, transform: brandingOpen ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }} />
            </button>
            {brandingOpen && (
              <div style={{ display: "grid", gap: 10, padding: "0 12px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", gap: 9, alignItems: "center", color: C.textSecondary, fontSize: 13, fontWeight: 700 }}>
                    <input type="checkbox" checked={usesAppLeaderboardBranding} onChange={(event) => { setSaved(false); setUseAppLeaderboardBranding(event.target.checked); }} />
                    Use app leaderboard colours
                  </label>
                  <button type="button" onClick={() => { setSaved(false); resetLiveDefaults(); }} style={{ minHeight: 34, padding: "7px 11px", borderRadius: 9, border: `1px solid ${C.border}`, background: C.bgElevated, color: C.textSecondary, fontSize: 12, fontWeight: 900, cursor: "pointer" }}>
                    Reset colours
                  </button>
                </div>
                <label htmlFor="live-dashboard-logo-url" style={{ color: C.textSecondary, fontSize: 12, fontWeight: 800 }}>
                  Logo URL
                  <input id="live-dashboard-logo-url" type="url" value={form.settings?.logoUrl || ""} onChange={(event) => { setSaveError(""); setLogoPreviewError(false); updateSetting("logoUrl", event.target.value.trim()); }} onBlur={() => setLogoTouched(true)} placeholder="https://example.com/logo.png" autoComplete="url" aria-describedby={shownLogoUrlError ? "live-dashboard-logo-error" : "live-dashboard-logo-help"} aria-invalid={Boolean(shownLogoUrlError)} style={{ ...inputStyle, marginTop: 5, color: C.text, background: C.bgElevated, borderColor: shownLogoUrlError ? C.danger : "rgba(148,163,184,0.35)" }} />
                  {shownLogoUrlError ? (
                    <span id="live-dashboard-logo-error" role="alert" style={{ display: "block", color: C.danger, fontSize: 12, lineHeight: 1.45, marginTop: 5 }}>{shownLogoUrlError}</span>
                  ) : (
                    <span id="live-dashboard-logo-help" style={{ display: "block", color: C.textMuted, fontSize: 12, fontWeight: 600, lineHeight: 1.45, marginTop: 5 }}>Leave blank to use the institute logo fallback.</span>
                  )}
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 10, background: C.bgCard, border: `1px solid ${C.border}` }}>
                  {previewLogoUrl && !logoPreviewError ? (
                    <img src={previewLogoUrl} alt="" onLoad={() => setLogoPreviewError(false)} onError={() => setLogoPreviewError(true)} style={{ width: 42, height: 42, borderRadius: 10, objectFit: "cover", border: `1px solid ${C.border}`, flexShrink: 0 }} />
                  ) : (
                    <div aria-hidden="true" style={{ width: 42, height: 42, borderRadius: 10, display: "grid", placeItems: "center", background: C.accentBgSubtle, color: C.accent, border: `1px solid ${C.accentBorder}`, flexShrink: 0, fontWeight: 900 }}>{(state?.workspace_name || "W")[0]}</div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: C.textSecondary, fontSize: 12, fontWeight: 900 }}>Live page logo</div>
                    <div style={{ color: logoPreviewError ? C.danger : C.textMuted, fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>{logoPreviewError ? "Could not load this logo. Check the image URL." : form.settings?.logoUrl ? "Using your custom logo URL." : state?.workspace_logo_url ? "Using the institute logo fallback." : "No logo set. Add a URL above to show one."}</div>
                  </div>
                </div>
                <div aria-label="Live dashboard colour preview" style={{ minHeight: 112, borderRadius: 10, padding: 14, overflow: "hidden", position: "relative", border: "1px solid rgba(148,163,184,0.22)", background: `linear-gradient(135deg, ${effectiveSettings.primaryColor}${previewIsLight ? "33" : "77"}, transparent 58%), ${effectiveSettings.backgroundColor}`, color: previewText }}>
                  <div aria-hidden="true" style={{ position: "absolute", inset: "auto 14px 14px auto", display: "flex", width: 116, height: 8, borderRadius: 999, overflow: "hidden" }}>
                    {[effectiveSettings.primaryColor, effectiveSettings.accentColor, previewSupport, previewHighlight].map((color, index) => <span key={`${color}-${index}`} style={{ flex: 1, background: color }} />)}
                  </div>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ color: previewMuted, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.6 }}>Live workspace</div>
                    <div style={{ color: previewText, fontSize: 18, fontWeight: 950, marginTop: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{state?.workspace_name || "Workspace"}</div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 18, padding: "7px 10px", borderRadius: 999, background: previewIsLight ? "rgba(255,255,255,0.74)" : "rgba(255,255,255,0.10)", border: `1px solid ${effectiveSettings.accentColor}` }}>
                      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 999, background: effectiveSettings.accentColor }} />
                      <span style={{ fontSize: 12, fontWeight: 900 }}>Leaderboard</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8 }}>
                  {[
                    ["primaryColor", "Primary", false],
                    ["accentColor", "Accent", false],
                    ["supportColor", "Support (optional)", true],
                    ["highlightColor", "Highlight (optional)", true],
                    ["backgroundColor", "Background", false],
                  ].map(([key, label, optional]) => (
                    <label key={key} style={{ color: usesAppLeaderboardBranding && !optional && key !== "backgroundColor" ? C.textMuted : C.textSecondary, fontSize: 12, fontWeight: 700 }}>
                      {label}
                      <div style={{ display: "grid", gridTemplateColumns: "44px minmax(0,1fr)", gap: 7, alignItems: "center", marginTop: 5 }}>
                        <input aria-label={`Live dashboard ${label.toLowerCase()} colour`} type="color" value={effectiveSettings[key] || (key === "supportColor" ? effectiveSettings.primaryColor : effectiveSettings.accentColor)} disabled={usesAppLeaderboardBranding && !optional && key !== "backgroundColor"} onChange={(event) => updateSetting(key, event.target.value)} style={{ width: 44, height: 36, opacity: usesAppLeaderboardBranding && !optional && key !== "backgroundColor" ? 0.58 : 1 }} />
                        <input aria-label={`${label} hex colour`} placeholder={optional ? "Optional" : undefined} value={effectiveSettings[key]} disabled={usesAppLeaderboardBranding && !optional && key !== "backgroundColor"} onChange={(event) => updateSetting(key, event.target.value)} onBlur={(event) => updateSetting(key, optional ? normalizeOptionalHexInput(event.target.value) : normalizeHexInput(event.target.value, DEFAULT_SETTINGS[key]))} style={{ ...colorTextStyle(C), opacity: usesAppLeaderboardBranding && !optional && key !== "backgroundColor" ? 0.58 : 1 }} />
                      </div>
                    </label>
                  ))}
                </div>
                <div style={{ color: C.textMuted, fontSize: 12, lineHeight: 1.5 }}>Support and highlight are optional. Leave them blank for a simple two-colour brand.</div>
              </div>
            )}
          </section>

          {saveError && !currentLogoUrlError && <div role="alert" style={{ padding: 10, borderRadius: 9, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: C.danger, fontSize: 12, lineHeight: 1.5 }}>{saveError}</div>}
          {saved && <div role="status" style={{ display: "flex", alignItems: "center", gap: 7, color: C.success, fontSize: 12, fontWeight: 800 }}><CheckCircle2 aria-hidden="true" size={16} /> Settings saved. Your live page is ready.</div>}
          <button type="button" disabled={saving} onClick={save} style={{ display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 7, justifySelf: "start", minHeight: 42, padding: "10px 15px", border: 0, borderRadius: 9, background: C.accent, color: "#fff", fontWeight: 850, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}>
            {saving && <Loader2 aria-hidden="true" size={16} style={{ animation: "spin 1s linear infinite" }} />}
            {saving ? "Saving..." : form.enabled ? "Save and publish" : "Save dashboard"}
          </button>
        </div>
      )}
    </section>
  );
}
